import React, { useState } from "react";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import FindReplaceOutlinedIcon from "@mui/icons-material/FindReplaceOutlined";
import { Link, NavLink } from "react-router-dom";
import { Box, Toolbar, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { RiBookMarkLine } from "react-icons/ri";
import { BiPieChartAlt2 } from "react-icons/bi";
import { BsSafe2 } from "react-icons/bs";
import { BsCalendar2Date } from "react-icons/bs";
import { VscGraphLine } from "react-icons/vsc";
import { RiMessage2Line } from "react-icons/ri";
import { useGetClassesQuery } from "../pages/classroom/classApiSlice";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../pages/auth/authSlice";
import { useGetSectionQuery } from "../pages/settings/sections/sectionsApiSlice";
import { useGetDepartmentQuery } from "../pages/settings/departments/departmentsApiSlice";
import { useGetTeacherSubjectsQuery } from "../pages/staff/staffApiSlice";
import { useIsPro } from "../../hooks/useIsPro";
import { useGetSchoolProfileQuery } from "../pages/settings/school/schoolApiSlice";
import {
  DashboardCustomize,
  DashboardRounded,
  SchoolOutlined,
} from "@mui/icons-material";

function SideNav({ drawerWidth, handleDrawerToggle, window, mobileOpen }) {
  const container =
    window !== undefined ? () => window().document.body : undefined;

  const theme = useTheme();
  const contrastText = theme.palette.primary.contrastText;

  const user = useSelector(selectCurrentUser);

  const { data: schoolProfile } = useGetSchoolProfileQuery(undefined, {
    skip: !user,
  });
  const schoolName = schoolProfile?.name || "Dashboard";

  const isPro = useIsPro();

  const userRoleNames = user?.roles
    ?.map((item) => item?.role?.name)
    .filter(Boolean);
  const isTeacher = userRoleNames?.includes("teacher") ?? false;

  const { data: myClasses } = useGetClassesQuery("all/mine");
  const { data: sections } = useGetSectionQuery("all");
  const { data: departments } = useGetDepartmentQuery("all");
  const { data: subjects } = useGetTeacherSubjectsQuery("all", {
    skip: !isTeacher,
  });

  const isClassManager = !!myClasses?.length;

  const userSections = sections?.filter(
    (section) => section?.head?.id === user?.id,
  );

  const userDepartments = departments?.filter(
    (department) => department?.head?.id === user?.id,
  );

  // determine the role of the user
  const isAdmin = userRoleNames?.includes("admin") ? true : false;
  const isWebmaster = userRoleNames?.includes("webmaster") ? true : false;

  const isSubjectTeacher = subjects?.length > 0;

  const isExaminer = userRoleNames?.includes("examiner") ? true : false;

  const isAccountant = userRoleNames?.includes("accountant") ? true : false;

  //check for agent user role and have templates as its nav
  const isAgent = userRoleNames?.includes("agent") ? true : false;

  const isParent = userRoleNames?.includes("parent") ? true : false;

  const isHeadofSection = userSections?.length > 0;

  const isHeadOfDepartment = userDepartments?.length > 0;

  const headOfSectionNavs = isHeadofSection
    ? userSections?.flatMap((section) => {
        //creates nav items for each existing section for the head of section
        return [
          {
            text: section.name + " report",
            icon: (
              <RiMessage2Line
                style={{
                  fontSize: "24px",
                  color: contrastText,
                  marginLeft: "2px",
                }}
              />
            ),
            link: `/section-report/${section.id}`,
          },
        ];
      })
    : [];

  const headOfDepartmentNavs = isHeadOfDepartment
    ? userDepartments?.flatMap((department) => [
        {
          text: department.name + " report",
          icon: (
            <RiMessage2Line
              style={{
                fontSize: "24px",
                color: contrastText,
                marginLeft: "2px",
              }}
            />
          ),
          link: `/department-report/${department.id}`,
        },
      ])
    : [];

  const teacherClassrooms =
    myClasses?.flatMap((classroom) => {
      //creates nav items for each existing class for the teacher
      return [
        {
          text: classroom.name + " report",
          icon: (
            <RiMessage2Line
              style={{
                fontSize: "24px",
                color: contrastText,
                marginLeft: "2px",
              }}
            />
          ),
          link: `/class-reportsheet/${classroom.id}`,
        },

        {
          text: classroom.name + " subjects",
          icon: (
            <RiBookMarkLine style={{ fontSize: "24px", color: contrastText }} />
          ),
          link: `/class-subjects/${classroom.id}`,
        },

        {
          text: classroom.name + " students",
          icon: (
            <GroupOutlinedIcon
              style={{ fontSize: "24px", color: contrastText }}
            />
          ),
          link: `/class-students/${classroom.id}`,
        },
      ];
    }) || [];

  const classManagerNavs = isClassManager
    ? // nav that are only available to classTeacher
      [
        {
          text: "manage attendance",
          icon: (
            <BsCalendar2Date
              style={{
                fontSize: "24px",
                color: contrastText,
                marginLeft: "2px",
              }}
            />
          ),
          link: "/manage-attendance",
        },
      ]
    : [];

  const subjectTeacherNavs = isSubjectTeacher
    ? [
        {
          text: "Score Manager",
          icon: (
            <VscGraphLine style={{ fontSize: "24px", color: contrastText }} />
          ),
          link: "/ca-manager",
        },
        {
          text: "quick entry",
          icon: (
            <BiPieChartAlt2 style={{ fontSize: "24px", color: contrastText }} />
          ),
          link: "/quick-entry",
        },
      ]
    : [];

  const teacherNavs = [
    // append the teacher navlist created above to the teacher navs
    ...teacherClassrooms,
    ...classManagerNavs,
    ...subjectTeacherNavs,
    classManagerNavs && {
      text: "Connect",
      icon: <ForumOutlinedIcon style={{ color: contrastText }} />,
      link: "/communication/feed",
    },

    // {
    //   text: "exam manager",
    //   icon: <BiPieChartAlt2 style={{ fontSize: "24px", color: "#ffffff" }} />,
    //   link: "/exam-manager",
    // },

    // {
    //   text: "Subject timetable",
    //   icon: (
    //     <BsCalendar2Date
    //       style={{ fontSize: "24px", color: "#ffffff", marginLeft: "2px" }}
    //     />
    //   ),
    //   link: "/subject-timetable",
    // },
    // {
    //   text: "Section Report",
    //   icon: (
    //     <BsCalendar2Date
    //       style={{ fontSize: "24px", color: "#ffffff", marginLeft: "2px" }}
    //     />
    //   ),
    //   link: "/section-report",
    // },
  ];

  const examOfficerNavs = isExaminer
    ? [
        {
          text: "class broadsheet",
          icon: (
            <BiPieChartAlt2 style={{ fontSize: "24px", color: contrastText }} />
          ),
          link: "/class-broadsheet",
        },
        {
          text: "class report",
          icon: (
            <VscGraphLine style={{ fontSize: "24px", color: contrastText }} />
          ),
          link: "/class-report",
        },
        {
          text: "student's report",
          icon: <AccountCircleOutlinedIcon style={{ color: contrastText }} />,
          link: "/student-report",
        },
        {
          text: "reconcile records",
          icon: <FindReplaceOutlinedIcon style={{ color: contrastText }} />,
          link: "/subject-reconciliation",
        },
      ]
    : [];

  const accountantNavs = [
    {
      text: "Payments Dashboard",
      icon: <AccountBalanceOutlinedIcon style={{ color: contrastText }} />,
      link: "/payments-dashboard",
    },
    {
      text: "Subscription",
      icon: <CardMembershipOutlinedIcon style={{ color: contrastText }} />,
      link: "/subscription",
    },
    {
      text: "Account Settings",
      icon: (
        <BsSafe2
          style={{
            fontSize: "24px",
            color: contrastText,
            marginLeft: "2px",
          }}
        />
      ),
      link: "/account-settings",
    },
    {
      text: "Fees Schedule",
      icon: (
        <AccountBalanceWalletOutlinedIcon style={{ color: contrastText }} />
      ),
      link: "/fees-schedule",
    },
    {
      text: "Invoices",
      icon: <DescriptionOutlinedIcon style={{ color: contrastText }} />,
      link: "/invoices",
    },
  ];

  const adminNavs = [
    {
      text: "Connect",
      icon: <ForumOutlinedIcon style={{ color: contrastText }} />,
      link: "/communication/feed",
    },
    {
      text: "staff",
      icon: <GroupOutlinedIcon style={{ color: contrastText }} />,
      link: "/staff",
    },
    {
      text: "classes",
      icon: <LanOutlinedIcon style={{ color: contrastText }} />,
      link: "/classes",
    },
    // {
    //   text: "houses",
    //   icon: <GoHome style={{ fontSize: "24px", color: "white" }} />,
    //   link: "/houses",
    // },
    {
      text: "Subjects",
      icon: (
        <RiBookMarkLine style={{ fontSize: "24px", color: contrastText }} />
      ),
      link: "/subjects",
    },
    {
      text: "students",
      icon: <SchoolOutlinedIcon style={{ color: contrastText }} />,
      link: "/students",
    },
    {
      text: "roles",
      icon: <BadgeOutlinedIcon style={{ color: contrastText }} />,
      link: "/roles",
    },
    {
      text: "settings",
      icon: <SettingsOutlinedIcon style={{ color: contrastText }} />,
      link: "/settings",
    },
    {
      text: "Score Log",
      icon: <ListAltOutlinedIcon style={{ color: contrastText }} />,
      link: "/score-log",
    },
    {
      text: "Activity Logs",
      icon: <ListAltOutlinedIcon style={{ color: contrastText }} />,
      link: "/activity-logs",
    },
    {
      text: "Promotions",
      icon: <SwapHorizOutlinedIcon style={{ color: contrastText }} />,
      link: "/session-transfer",
    },
    {
      text: "Subscription",
      icon: <CardMembershipOutlinedIcon style={{ color: contrastText }} />,
      link: "/subscription",
    },
  ];

  const agentNavs = [
    {
      text: "school Information",
      icon: <DashboardRounded style={{ color: contrastText }} />,
      link: "/school-information",
    },
    {
      text: "templates",
      icon: <LanOutlinedIcon style={{ color: contrastText }} />,
      link: "/templates",
    },
    {
      text: "school import",
      icon: <CloudDownloadOutlinedIcon style={{ color: contrastText }} />,
      link: "/school-import",
    },
  ];

  const parentNavs = [
    {
      text: "Parent Portal",
      icon: <FamilyRestroomOutlinedIcon style={{ color: contrastText }} />,
      link: "/parent",
    },
    {
      text: "Connect",
      icon: <ForumOutlinedIcon style={{ color: contrastText }} />,
      link: "/communication/feed",
    },
  ];

  const webmasterNavs = [
    {
      text: "Website CMS",
      icon: <LanguageOutlinedIcon style={{ color: contrastText }} />,
      link: "/cms",
    },
  ];

  const [openSection, setOpenSection] = useState(() => {
    const first = [
      isAdmin && "admin",
      (isTeacher || isHeadofSection || isHeadOfDepartment) && "teacher",
      isExaminer && "examiner",
      isAccountant && "accountant",
      isAgent && "agent",
      isParent && "parent",
      isWebmaster && isPro && "webmaster",
    ].find(Boolean);
    return first || null;
  });

  const roleGroups = [
    { key: "admin", label: "Admin", show: isAdmin, navs: adminNavs },
    {
      key: "teacher",
      label: "Teacher",
      show: isTeacher || isHeadofSection || isHeadOfDepartment,
      navs: [...teacherNavs, ...headOfSectionNavs, ...headOfDepartmentNavs],
    },
    {
      key: "examiner",
      label: "Examiner",
      show: isExaminer,
      navs: examOfficerNavs,
    },
    {
      key: "accountant",
      label: "Accountant",
      show: isAccountant,
      navs: accountantNavs,
    },
    { key: "agent", label: "Agent", show: isAgent, navs: agentNavs },
    { key: "parent", label: "Parent", show: isParent, navs: parentNavs },
    {
      key: "webmaster",
      label: "Webmaster",
      show: (isWebmaster || isAdmin) && isPro,
      navs: webmasterNavs,
    },
  ].filter((g) => g.show && g.navs.length > 0);

  const isMultiRole = roleGroups.length > 1;

  const toggleSection = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  const drawer = (
    <Box
      sx={{
        "& .MuiDivider-root": {
          backgroundColor: contrastText,
        },
      }}
    >
      <Toolbar sx={{ justifyContent: "center" }}>
        <Link to="/" style={{ textDecoration: "none", color: contrastText }}>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: contrastText, fontWeight: 500 }}
          >
            {schoolName}
          </Typography>
        </Link>
      </Toolbar>
      <Divider />
      <List
        sx={{
          fontSize: "12px",
          "& .MuiTypography-root": {
            fontSize: "12px",
            fontWeight: 600,
            color: contrastText,
          },
          "& a": {
            color: contrastText,
            textDecoration: "none",
            margin: "0 15px",
            borderRadius: "4px",
            width: "100%",
            textTransform: "uppercase",
          },
          "& .MuiSvgIcon-root": {
            color: contrastText,
            fontSize: "27px",
          },
          "& .MuiListItemIcon-root": {
            minWidth: "fit-content",
            marginRight: "15px",
          },
        }}
      >
        {isMultiRole ? (
          roleGroups.map((group) => (
            <React.Fragment key={group.key}>
              <ListItem disablePadding sx={{ margin: "5px 0" }}>
                <ListItemButton
                  onClick={() => toggleSection(group.key)}
                  sx={{
                    color: contrastText,
                    borderRadius: "4px",
                    mx: "15px",
                    px: "8px",
                  }}
                >
                  <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        opacity: 0.75,
                      },
                    }}
                  />
                  {openSection === group.key ? (
                    <ExpandLessIcon
                      sx={{ color: contrastText, fontSize: "20px" }}
                    />
                  ) : (
                    <ExpandMoreIcon
                      sx={{ color: contrastText, fontSize: "20px" }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse
                in={openSection === group.key}
                timeout="auto"
                unmountOnExit
              >
                <List disablePadding>
                  {group.navs.map((item, index) => (
                    <ListItem
                      key={index}
                      disablePadding
                      sx={{ margin: "5px 0" }}
                    >
                      <NavLink
                        to={item.link}
                        style={({ isActive }) =>
                          isActive
                            ? { backgroundColor: alpha(contrastText, 0.23) }
                            : undefined
                        }
                      >
                        <ListItemButton>
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))
        ) : (
          <>
            {isAdmin &&
              adminNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <Box
                    component={NavLink}
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </Box>
                </ListItem>
              ))}

            {isTeacher &&
              teacherNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}

            {isExaminer &&
              examOfficerNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}

            {isHeadofSection &&
              headOfSectionNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}

            {isHeadOfDepartment &&
              headOfDepartmentNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}

            {isAccountant &&
              accountantNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}

            {isAgent &&
              agentNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}

            {isParent && <Divider sx={{ my: 1 }} />}
            {isParent &&
              parentNavs.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ margin: "5px 0" }}>
                  <NavLink
                    to={item.link}
                    style={({ isActive }) =>
                      isActive
                        ? { backgroundColor: alpha(contrastText, 0.23) }
                        : undefined
                    }
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
              ))}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidth },
        flexShrink: { sm: 0 },
        backgroundColor: "blue",
      }}
      aria-label="mailbox folders"
    >
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default SideNav;
